import type { AwsS3Part } from '@uppy/aws-s3'
import { Environment } from '../../../environment.ts'
import type { IFileUploadLocation } from '../../../Model/File/IFileUploadLocation.ts'
import type { EItemType } from '../../../Model/Sample/ISample.ts'
import { tracingAxios } from '../../../Repository/Sentry.ts'

/*
 * The pool upload endpoints as the classic upload page (`UppyProvider`) calls them, which inlines the same
 * requests. Kept identical so both paths register, sign, complete and verify files the same way.
 */

const API = () => Environment.API_URL
const credentials = { withCredentials: true }

/** Registers a file for upload; empty when the pool already holds that name verified. */
export const registerPoolFile = async (
  orderId: string,
  poolId: string,
  fileName: string,
  contentType: EItemType
): Promise<IFileUploadLocation | undefined> => {
  const res = await tracingAxios.post<IFileUploadLocation[]>(
    `${API()}/api/orders/${orderId}/pools/${poolId}/file-urls`,
    { files: [{ fileName, encodedMd5: '', contentType }] },
    credentials
  )
  return res.data[0]
}

/**
 * Idempotent. Single-PUT uploads need it; multipart uploads are verified by their completion already.
 * Empty when the server cannot find the uploaded object.
 */
export const verifyPoolFile = async (orderId: string, poolId: string, fileId: string): Promise<IFileUploadLocation | undefined> => {
  const res = await tracingAxios.post<IFileUploadLocation | null>(
    `${API()}/api/orders/${orderId}/pools/${poolId}/file-urls/${fileId}/verify`,
    {},
    credentials
  )
  return res.data ?? undefined
}

/** The pool's uploaded and verified files. */
export const completedPoolFiles = async (orderId: string, poolId: string): Promise<IFileUploadLocation[]> => {
  const res = await tracingAxios.get<IFileUploadLocation[]>(
    `${API()}/api/orders/${orderId}/pools/${poolId}/file-urls/completed`,
    credentials
  )
  return res.data
}

export const startMultipartUpload = async (
  pendingId: string,
  contentType: string | undefined
): Promise<{ uploadId: string; key: string }> => {
  const res = await tracingAxios.post(`${API()}/s3/multipart/start`, { uploadId: pendingId, contentType }, credentials)
  return res.data
}

export const listMultipartParts = async (uploadId: string, key: string, pendingId: string | undefined): Promise<AwsS3Part[]> => {
  const res = await tracingAxios.get(`${API()}/s3/multipart/${uploadId}/parts`, { params: { key, pendingId }, ...credentials })
  return res.data as AwsS3Part[]
}

export const abortMultipartUpload = async (uploadId: string, key: string, pendingId: string | undefined): Promise<void> => {
  await tracingAxios.delete(`${API()}/s3/multipart/${uploadId}`, { params: { key, pendingId }, ...credentials })
}

export const completeMultipartUpload = async (
  pendingId: string | undefined,
  uploadId: string,
  key: string,
  parts: AwsS3Part[]
): Promise<{ location: string }> => {
  const res = await tracingAxios.post(`${API()}/s3/multipart/complete`, { pendingId, uploadId, key, parts }, credentials)
  return { location: res.data.location }
}

export const signMultipartPart = async (uploadId: string, partNumber: number, pendingId: string | undefined): Promise<{ url: string }> => {
  const res = await tracingAxios.get(`${API()}/s3/multipart/${uploadId}/${partNumber}`, { params: { pendingId }, ...credentials })
  return { url: res.data.url }
}
